import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, MessageSquare, MapPin, Plane, BookOpen, X, Crosshair } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../api";
import { DASH } from "../constants/testIds";

// New order per spec — active modules first, then a "Coming Soon" group.
const ACTIVE_TILES = [
  { key: "murshid",     label: "Ask Murshid",  icon: BookOpen,      path: "/murshid",     testId: DASH.tileMurshid },
  { key: "calendar",    label: "Calendar",     icon: Calendar,      path: "/calendar",    testId: DASH.tileCalendar },
  { key: "geolocation", label: "Geolocation",  icon: MapPin,        path: "/geolocation", testId: DASH.tileGeo },
];

const COMING_SOON_TILES = [
  { key: "hajj",        label: "Hajj & Umrah", icon: Plane,         path: "/hajj",        testId: DASH.tileHajj },
  { key: "messaging",   label: "Messaging",    icon: MessageSquare, path: "/messaging",   testId: DASH.tileMessaging },
];

// localStorage cache key prefix — keyed by user_id + date per spec (v2: round-up fix)
const PRAYER_CACHE_PREFIX = "prayer_times_";
const PRAYER_CACHE_VERSION = "_v2";
const cacheKey = (uid, date) => `${PRAYER_CACHE_PREFIX}${uid || "anon"}_${date}${PRAYER_CACHE_VERSION}`;

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prayer, setPrayer] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [askGPS, setAskGPS] = useState(false);

  // POST-based prayer-times fetch (uses location stored on the user's profile)
  useEffect(() => {
    let cancelled = false;
    const todayKey = new Date().toISOString().slice(0, 10);
    const k = cacheKey(user?.id, todayKey);

    // Try cache first
    try {
      const c = JSON.parse(localStorage.getItem(k) || "null");
      if (c && c.data?.prayer_times) { setPrayer(c.data); return; }
    } catch { /* noop */ }

    const cacheAndSet = (data) => {
      if (cancelled || !data?.prayer_times) return;
      setPrayer(data);
      try {
        localStorage.setItem(k, JSON.stringify({ date: todayKey, data }));
        // Evict stale prayer-times keys (any with a date prefix that isn't today)
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith(PRAYER_CACHE_PREFIX) && key !== k) {
            localStorage.removeItem(key);
          }
        }
      } catch { /* noop */ }
    };

    const fetchTimes = async () => {
      try {
        const r = await api.post("/prayer/times", {});
        cacheAndSet(r.data);
      } catch { /* user might not have location yet */ }
    };

    // Ask GPS once per session if user has no saved location
    const gpsAsked = sessionStorage.getItem("dnya_gps_asked");
    if (!gpsAsked && navigator.geolocation && !(user?.latitude || user?.city)) {
      sessionStorage.setItem("dnya_gps_asked", "1");
      setAskGPS(true);
    } else {
      fetchTimes();
    }

    return () => { cancelled = true; };
  }, [user?.id]);

  const acceptGPS = () => {
    setAskGPS(false);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      try {
        // Light reverse geocode → save to profile so POST /prayer/times picks it up
        let city = "Unknown", country = "Unknown";
        try {
          const j = await (await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10`, { headers: { "Accept-Language": "en" } })).json();
          city = j?.address?.city || j?.address?.town || j?.address?.village || j?.address?.state || "Unknown";
          country = j?.address?.country || "Unknown";
        } catch { /* noop */ }
        await api.post("/profile/location", { country, city, latitude, longitude });
      } catch { /* noop */ }
      try {
        const r = await api.post("/prayer/times", {});
        const todayKey = new Date().toISOString().slice(0, 10);
        setPrayer(r.data);
        localStorage.setItem(cacheKey(user?.id, todayKey), JSON.stringify({ date: todayKey, data: r.data }));
      } catch { /* noop */ }
    }, () => {
      // Denied — show whatever the backend can serve from profile (or 502)
      api.post("/prayer/times", {}).then((r) => setPrayer(r.data)).catch(() => { /* noop */ });
    });
  };

  const denyGPS = () => {
    setAskGPS(false);
    api.post("/prayer/times", {}).then((r) => setPrayer(r.data)).catch(() => { /* noop */ });
  };

  const first = user?.full_name?.split(" ")[0] || "Friend";

  const nextPrayer = (() => {
    if (!prayer?.prayer_times) return null;
    const now = new Date();
    const today = now.toISOString().slice(0, 10);
    let upcoming = null;
    for (const [name, hm] of Object.entries(prayer.prayer_times)) {
      const [h, m] = hm.split(":").map(Number);
      const d = new Date(`${today}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`);
      if (d > now && (!upcoming || d < upcoming.time)) upcoming = { name, time: d, hm };
    }
    return upcoming;
  })();

  const countdown = (() => {
    if (!nextPrayer) return null;
    const diff = nextPrayer.time - new Date();
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return `${nextPrayer.name} in ${h}h ${m}m`;
  })();

  return (
    <div className="dnya-reveal">
      <div data-testid={DASH.greeting} style={{ marginBottom: 8 }}>
        <h1 className="font-dnya" style={{ fontSize: 26, color: "var(--dnya-high-1)", margin: 0 }}>
          Assalamu Alaikum, {first}
        </h1>
        <div style={{ color: "var(--dnya-high-2)", fontSize: 13, marginTop: 4 }}>
          {prayer ? `${prayer.date_gregorian} · ${prayer.date_hijri}` : "Loading dates..."}
        </div>
        {countdown && (
          <div data-testid="dashboard-next-prayer" style={{ marginTop: 6, fontSize: 12, color: "#4a9a4a", fontStyle: "italic" }}>
            {countdown}
          </div>
        )}
      </div>

      {askGPS && (
        <div style={{ background: "var(--dnya-mosaic-1)", color: "var(--dnya-high-1)", padding: 14, borderRadius: 12, marginTop: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <Crosshair size={18} />
          <span style={{ flex: 1, fontSize: 13, minWidth: 200 }}>
            Allow location access for accurate prayer times in your area Insha&apos;Allah.
          </span>
          <button data-testid="dashboard-gps-allow" onClick={acceptGPS} style={{ padding: "8px 16px", borderRadius: 8, background: "var(--dnya-high-1)", color: "var(--dnya-bg)", border: 0, fontWeight: 600, cursor: "pointer", minHeight: 36 }}>
            Allow
          </button>
          <button onClick={denyGPS} data-testid="dashboard-gps-deny" style={{ padding: "8px 16px", borderRadius: 8, background: "transparent", color: "var(--dnya-high-1)", border: `1px solid var(--dnya-high-1)`, cursor: "pointer", minHeight: 36 }}>
            Not now
          </button>
        </div>
      )}

      {!dismissed && !askGPS && (
        <div style={{ background: "var(--dnya-mosaic-1)", color: "var(--dnya-high-1)", padding: "12px 14px", borderRadius: 12, display: "flex", alignItems: "center", gap: 10, margin: "14px 0 22px" }}>
          <span style={{ flex: 1, fontSize: 13 }}>
            Insha&apos;Allah may your day be filled with barakah. Try saying &quot;find the nearest mosque&quot; or &quot;schedule iftar tomorrow&quot;.
          </span>
          <button onClick={() => setDismissed(true)} aria-label="Dismiss"
            style={{ background: "transparent", border: 0, color: "var(--dnya-high-1)", cursor: "pointer", minWidth: 44, minHeight: 44, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <X size={18} />
          </button>
        </div>
      )}

      <div data-testid={DASH.prayerWidget} style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6, marginBottom: 28 }}>
        {prayer?.prayer_times ? Object.entries(prayer.prayer_times).map(([name, t]) => {
          const isNext = nextPrayer?.name === name;
          return (
            <div
              key={name}
              data-testid={`prayer-card-${name.toLowerCase()}`}
              style={{
                minWidth: 120, padding: "14px 16px", borderRadius: 12,
                background: "#0f3d0f",
                border: isNext ? "1px solid #CEF0CE" : "1px solid #1a5c1a",
                boxShadow: isNext ? "0 0 0 1px rgba(206,240,206,0.35), 0 0 14px rgba(206,240,206,0.25)" : "none",
                transition: "border-color 200ms ease, box-shadow 200ms ease",
                flexShrink: 0,
              }}
            >
              <div style={{ fontSize: 11, color: "#CEF0CE", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>{name}</div>
              <div style={{ fontSize: 16, color: "#ffffff", marginTop: 6, fontWeight: 500 }}>{t}</div>
              {isNext && <div style={{ fontSize: 11, color: "#4a9a4a", marginTop: 4, fontStyle: "italic" }}>{countdown}</div>}
            </div>
          );
        }) : (
          <div style={{ color: "var(--dnya-high-2)", fontSize: 13 }}>Bismillah... loading prayer times</div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
        {ACTIVE_TILES.map((t, i) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={t.testId}
              onClick={() => navigate(t.path)}
              className={`dnya-reveal dnya-reveal-d${(i % 6) + 1}`}
              style={{
                background: "var(--dnya-card)",
                border: `1px solid var(--dnya-mosaic-1)`,
                borderRadius: 14,
                padding: 22,
                color: "var(--dnya-text)",
                cursor: "pointer",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
                transition: "transform 150ms ease, border-color 150ms ease",
                minHeight: 140,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--dnya-mosaic-2)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--dnya-mosaic-1)"; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              <div style={{ width: 52, height: 52, borderRadius: "50%", background: "var(--dnya-mosaic-1)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--dnya-high-1)" }}>
                <Icon size={24} />
              </div>
              <div className="font-dnya" style={{ color: "var(--dnya-high-1)", fontSize: 16 }}>{t.label}</div>
            </button>
          );
        })}
      </div>

      {/* Coming soon section */}
      <div
        data-testid="dashboard-coming-soon-label"
        style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: "var(--text-muted)",
          padding: "24px 4px 8px",
          fontWeight: 500,
        }}
      >
        Coming Soon
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
        {COMING_SOON_TILES.map((t) => {
          const Icon = t.icon;
          return (
            <div
              key={t.key}
              data-testid={t.testId}
              aria-disabled="true"
              style={{
                position: "relative",
                background: "var(--dnya-card)",
                border: `1px solid var(--dnya-mosaic-1)`,
                borderRadius: 14,
                padding: 22,
                color: "var(--dnya-text)",
                cursor: "not-allowed",
                pointerEvents: "none",
                display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
                minHeight: 140,
                opacity: 0.5,
                overflow: "hidden",
              }}
            >
              <span
                data-testid={`dashboard-soon-${t.key}`}
                style={{
                  position: "absolute", top: 0, right: 0,
                  fontSize: 9,
                  background: "#1a5c1a",
                  color: "#CEF0CE",
                  borderRadius: "0 8px 0 8px",
                  padding: "2px 6px",
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                Soon
              </span>
              <div style={{ width: 52, height: 52, borderRadius: "50%", background: "var(--dnya-mosaic-1)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--dnya-high-1)" }}>
                <Icon size={24} />
              </div>
              <div className="font-dnya" style={{ color: "var(--dnya-high-1)", fontSize: 16 }}>{t.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
